import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { ResponsiveMode, Dropdown, IDropdownOption, TooltipHost, Stack, IStackTokens, Spinner, SpinnerSize, FontIcon, IComboBox, IComboBoxOption, ComboBox } from "@fluentui/react";
import { RootState } from "../../redux";
import { connect } from "react-redux";
import { DatasourceService } from "../../services/DatasourceService";
import { DataSourcePanel } from "./datasourcePanel";
import { DataSourceDropdownInitializer } from "../helpers/datasourceDropdownInitializer";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { useClientTypeContext } from "../hooks/useClientTypeContext";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { SharePointInputTypes, SharePointMetadataBooleans } from "../../config/sharepointConstants";

const stackTokens: IStackTokens = {
  childrenGap: 5
};

const getValueAsArray = (value: any): any[] => {
  return Array.isArray(value)
    ? value
    : (value
      ? (value as string)?.split(",")?.filter(x => x)?.map(x => x.trim() ?? [])
      : []);
}

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: any) => {
  const options = Array.isArray(defaultOptions) ? defaultOptions : [];
  if (allowFreeForm && Array.isArray(options) && inputValue) {
    const initialSelection: any[] = getValueAsArray(inputValue);
    for (let i = 0; i < initialSelection.length; i++) {
      const selectedValue = initialSelection[i] as string;
      if (!options.some(option => option.key && ((option.key as string).trim().toLowerCase() === selectedValue.trim().toLowerCase()))) {
        options.push({ key: selectedValue, text: selectedValue });
      }
    }
  }
  return options;
}

const mapStateToProps = (state: RootState) => {
  return {
    datasourceItems: state.tenantInfo.datasources,
    currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
  };
};

interface DatasourceDropdownMultipleReduxProps extends InputProps, ReduxProps { }
type ReduxProps = ReturnType<typeof mapStateToProps>;

export const UnconnectedDataSourceMultipleDropdown: React.FunctionComponent<DatasourceDropdownMultipleReduxProps> = ({
  displayName,
  isRequired,
  error,
  updateInputValidationStatus,
  inputValidator,
  datasourceItems,
  currentDirectory,
  inputValue,
  containerDisplayNames
}: DatasourceDropdownMultipleReduxProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [datasources, setDatasources] = React.useState<IDropdownOption[]>([]);
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
  const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
  const [boundedField, setBoundedField] = React.useState<string>("");
  const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
  const dataSourceService = new DatasourceService();
  const dataSourceHelper = new DatasourceHelper(dataSourceService);
  const clientTypes = useClientTypeContext(containerDisplayNames?.siteId ?? currentDirectory?.siteId);
  // useMemo, NOT a one-shot useState initializer: the client-type context can settle after
  // mount, and a captured verdict would silently ignore it. getInputDataSource returns the
  // same object while the verdict is unchanged, so downstream [dataSourceItem] deps are stable.
  const dataSourceItem = React.useMemo(
    () => DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""], clientTypes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientTypes]
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const allowCustomValues: boolean = React.useMemo(() => dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.allowCustomValues,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSourceItem]);

  const isFirstRun = React.useRef(true);
  // Same overlap machinery as datasourceDropdown: the re-keyed load body is re-entrant, so
  // stale-run writes are epoch-discarded and a master push outranks an in-flight load.
  const loadEpochRef = React.useRef<number>(0);
  const optionsPushEpochRef = React.useRef<number>(0);
  React.useEffect(() => {
    if (!isFirstRun.current) {
      try {
        // Same guards as the load effect's callback: this reload is for the CURRENT
        // datasource - a verdict change (epoch) or a newer master push (pushEpoch) while it
        // is in flight makes its landing stale, and writing it would resurrect an excluded
        // datasource's options or clobber a narrowed list.
        const epoch: number = loadEpochRef.current;
        const pushEpochAtStart: number = optionsPushEpochRef.current;
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, false);
        dropdownInitializer.initialize((options) => setDatasources(prevOptions =>
          (epoch === loadEpochRef.current && pushEpochAtStart === optionsPushEpochRef.current)
            ? getOptions(allowCustomValues, options, inputValue?.value)
            : prevOptions));
      } catch (e) {
        logger(e, "dataSourceMultipleDropdown.tsx (resetDatasourceOptionsToggle)");
      }
    }
  }, [inputValue?.resetDatasourceOptionsToggle]);

  React.useEffect(() => {
    if (Array.isArray(inputValue?.updatedDatasourceOptions)) {
      try {
        optionsPushEpochRef.current++;
        setDatasources(inputValue.updatedDatasourceOptions.map(option => ({ key: option, text: option })));
      } catch (e) {
        logger(e, "dataSourceMultipleDropdown.tsx (updatedDatasourceOptions)");
      }
    }
  }, [inputValue?.updatedDatasourceOptions]);

  React.useEffect(() => {
    const epoch: number = ++loadEpochRef.current;
    const pushEpochAtStart: number = optionsPushEpochRef.current;
    // Excluded verdict (datasource -> undefined) on a re-fire: the initializer no-ops on an
    // undefined datasource, so the previous datasource's options must be cleared explicitly.
    if (!isFirstRun.current && !dataSourceItem) {
      setDatasources(getOptions(allowCustomValues, [], inputValue?.value));
    }
    const setupAsyncWrapper = async () => {
      setIsLoading(true);
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, true);
        const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize((options) => setDatasources(prevOptions =>
          (epoch === loadEpochRef.current && pushEpochAtStart === optionsPushEpochRef.current)
            ? getOptions(allowCustomValues, options, inputValue?.value)
            : prevOptions))]);
        if (epoch !== loadEpochRef.current) {
          return;
        }
        if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
        const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, boundName);
        setBoundedField(boundedFieldName);
      } catch (error) {
        logger(error, "dataSourceMultipleDropdown.tsx (useEffect 1)");
      }
      if (epoch === loadEpochRef.current) {
        setIsLoading(false);
      }
    };

    setupAsyncWrapper();
    // Keyed on the resolved datasource so a client-type verdict that settles after mount
    // reloads the options instead of being silently ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceItem]);

  React.useEffect(() => {
    if (inputValue?.value) {
      try {
        setSelectedKeys(getValueAsArray(inputValue?.value));
      } catch (e) {
        logger(e, "dataSourceMultipleDropdown.tsx (inputValue?.value)");
      }
    }
  }, [inputValue?.value]);

  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      const valuesAsArray: any[] = getValueAsArray(inputValue?.value);
      // let selectedItems = [...valuesAsArray, ...(selectedKeys?.filter(x => !valuesAsArray.some(v => v == x)))];
      let selectedItems = [...valuesAsArray];
      updateInputValidationStatus(inputValidator.validate(inputValue?.xml?.Type === SharePointInputTypes.multiChoice ? selectedItems : selectedItems.join(", ")));
    } catch (e) {
      logger(e, "dataSourceMultipleDropdown.tsx (useEffect 2)");
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const closePanel = () => {
    setIsOpen(false);
  };

  const updateSearchInputs = (index: number, value: string) => {
    DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
  };

  const openPanel = async () => {
    //updateSearch(selectedKeys && selectedKeys.length === 1 ? selectedKeys[0] : "");
    setIsOpen(true);
  };

  const updateInputValue = (newValue: string) => {
    const valueAsArray: string[] = newValue ? [newValue] : [];
    setSelectedKeys(valueAsArray);
    updateInputValidationStatus(inputValidator.validate(inputValue?.xml?.Type === SharePointInputTypes.multiChoice ? valueAsArray : valueAsArray.join(", ")));
    updateSearch(newValue);
  };

  // const HandleOnBlur = (event): void => {
  //   updateInputValidationStatus(inputValidator.validate(inputValue?.xml?.Type === SharePointInputTypes.multiChoice ? selectedKeys : selectedKeys.join(", ")));
  // };

  const updateSearch = (newValue: string) => {
    const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
    updateSearchInputs(index, newValue);
  };

  const onChangeHandle = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      let selectedItems = item.selected
        ? [...selectedKeys, item.key as string]
        : selectedKeys.filter(key => key !== item.key);

      updateInputValidationStatus(inputValidator.validate(inputValue?.xml?.Type === SharePointInputTypes.multiChoice ? selectedItems : selectedItems.join(", ")));
      setSelectedKeys(selectedItems);
      updateSearch(selectedItems && selectedItems.length === 1 ? selectedItems[0] : "");
    }
  };

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let selected = option?.selected;
      if (!option && !value?.trim()) return;

      if (!option && value) {
        selected = true;
        option = { key: value, text: value };
        setDatasources(prevOptions => [...prevOptions, option]);
      }

      if (option) {
        const selection = selected ? [...(selectedKeys || []), option!.key as string] : selectedKeys?.filter(k => k !== option!.key);
        updateInputValidationStatus(inputValidator.validate(inputValue?.xml?.Type === SharePointInputTypes.multiChoice ? selection : selection.join(", ")));
        setSelectedKeys(selection);
        updateSearch(selection && selection.length === 1 ? selection[0] : "");
      }
    },
    [selectedKeys]
  );

  const input = allowCustomValues
    ?
    <ComboBox
      multiSelect
      allowFreeform
      useComboBoxAsMenuWidth
      placeholder={languageStrings.SelectOption}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={selectedKeys}
      label={displayName}
      options={datasources}
      onChange={onComboChange}
      // onBlur={HandleOnBlur}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKeys={selectedKeys}
      placeholder={languageStrings.SelectOption}
      options={datasources}
      onChange={onChangeHandle}
      // onBlur={HandleOnBlur}
      required={isRequired()}
      errorMessage={error}
      multiSelect
      className="xb-sp-input"
    // responsiveMode={ResponsiveMode.small}
    />;

  return (
    <>
      <Stack horizontal verticalAlign="start" tokens={stackTokens}>
        <Stack.Item grow={1}>{input}</Stack.Item>
        {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
          {isLoading
            ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
            : <div className="xb-search-icon-container">
              <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                <FontIcon iconName="Search" onClick={openPanel} />
              </TooltipHost>
            </div>}
        </Stack.Item>}
      </Stack>
      <DataSourcePanel
        displayName={dataSourceItem?.name}
        closePanel={closePanel}
        isOpen={isOpen}
        datasourceItem={dataSourceItem}
        datasourceService={dataSourceService}
        searchInputs={searchInputs}
        updateSearchInputs={updateSearchInputs}
        updateInputValue={updateInputValue}
        boundedField={boundedField}
      />
    </>
  );
};

export const DataSourceMultipleDropdown = connect(mapStateToProps, null)(UnconnectedDataSourceMultipleDropdown);
